"use client"

import React, { useState, useEffect, useRef } from "react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useDemoSocket } from "@/hooks/useDemoSocket"
import { useDemoLimit } from "@/hooks/useDemoLimit"
import { useDemoStore } from "@/store/demoStore"
import { UpgradeModal } from "./UpgradeModal"
import {
  Copy,
  X,
  File as FileIcon,
  ChevronDown,
  MessageSquare,
  Plus,
  FileSearch,
  BookOpen,
  TestTube,
  Zap,
  RefreshCw,
  Bug,
  Shield,
  Lightbulb,
  Search,
  HelpCircle,
  Code,
  Layers,
  Network,
  Package,
} from "lucide-react"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation"
import {
  Message,
  MessageContent,
  MessageResponse,
  MessageActions,
  MessageAction,
} from "@/components/ai-elements/message"
import {
  PromptInput,
  PromptInputProvider,
  PromptInputTextarea,
  PromptInputFooter,
  PromptInputSubmit,
  usePromptInputController,
} from "@/components/ai-elements/prompt-input"
import { ThinkingRow } from "./ThinkingRow"
import { MessageAttachmentsHover } from "./MessageAttachmentsHover"
import { ExplorerChatEmpty } from "./ExplorerChatEmpty"
import type { PromptInputTextareaProps } from "@/components/ai-elements/prompt-input"

// Wrapper component to handle truncation inside PromptInputProvider
const PromptInputTextareaWithTruncate: React.FC<PromptInputTextareaProps> = (props) => {
  const controller = usePromptInputController()
  
  return (
    <PromptInputTextarea
      {...props}
      onChange={(e) => {
        const value = e.currentTarget.value
        // Truncate if exceeds limit (handles paste events that bypass maxLength)
        if (value.length > 1000) {
          const truncated = value.substring(0, 1000)
          controller.textInput.setInput(truncated)
          // Also update textarea directly for immediate feedback
          e.currentTarget.value = truncated
          e.currentTarget.dispatchEvent(new Event('input', { bubbles: true }))
        } else {
          controller.textInput.setInput(value)
          props.onChange?.(e)
        }
      }}
    />
  )
}

interface SuggestedFilePromptsProps {
  attachmentCount: number
}

const SuggestedFilePrompts: React.FC<SuggestedFilePromptsProps> = ({ attachmentCount }) => {
  const controller = usePromptInputController()

  const prompts = [
    { label: "Review file", text: "Review this file", icon: FileSearch },
    { label: "Explain file", text: "Explain what this file does", icon: BookOpen },
    { label: "Document file", text: "Generate documentation for this file", icon: FileIcon },
    { label: "Generate tests", text: "Generate unit tests for this file", icon: TestTube },
    { label: "Optimize file", text: "Optimize this file for better performance", icon: Zap },
    { label: "Refactor file", text: "Refactor this file to improve code quality", icon: RefreshCw },
    { label: "Find bugs", text: "Find potential bugs and issues in this file", icon: Bug },
    { label: "Add error handling", text: "Add comprehensive error handling to this file", icon: Shield },
    { label: "Suggest improvements", text: "Suggest improvements for this file", icon: Lightbulb },
    { label: "Review security", text: "Review this file for security vulnerabilities", icon: Shield },
  ]

  const handlePromptSelect = (text: string) => {
    controller.textInput.setInput(text)
    setTimeout(() => {
      const textarea = document.querySelector('textarea[name="message"]') as HTMLTextAreaElement | null
      if (textarea) {
        textarea.focus()
        textarea.setSelectionRange(text.length, text.length)
      }
    }, 0)
  }

  return (
    <div className="mb-3">
      <div className="flex flex-wrap gap-2">
        {prompts.map((prompt) => {
          const Icon = prompt.icon
          return (
            <Button
              key={prompt.label}
              variant="outline"
              size="sm"
              className="h-8 text-xs transition-colors"
              onClick={() => handlePromptSelect(prompt.text)}
            >
              <Icon className="h-3 w-3 mr-1.5" />
              {prompt.label}
            </Button>
          )
        })}
      </div>
    </div>
  )
}

const SuggestedGeneralPrompts: React.FC<{ onSelectChat: (chatId: string) => void }> = ({ onSelectChat }) => {
  // Show only the first 3 example chats
  const exampleChats = useDemoStore((state) => state.exampleChats)
  const chatsToShow = exampleChats.slice(0, 3)

  const getIcon = (chatId: string) => {
    if (chatId.includes("code-explanation")) return Code
    if (chatId.includes("onboarding")) return BookOpen
    if (chatId.includes("oss")) return Layers
    return MessageSquare
  }

  return (
    <div className="mb-3">
      <div className="flex flex-wrap-reverse gap-2">
        {chatsToShow.map((chat) => {
          const Icon = getIcon(chat.id)
          return (
            <Button
              key={chat.id}
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs transition-colors"
              onClick={() => onSelectChat(chat.id)}
            >
              <Icon className="h-3 w-3 mr-1.5" />
              {chat.title}
            </Button>
          )
        })}
      </div>
    </div>
  )
}

interface DemoChatProps {
  onSignup: () => void
  hideTitle?: boolean
  disableUpgradeModal?: boolean
}

export const DemoChat: React.FC<DemoChatProps> = ({ onSignup, hideTitle = false, disableUpgradeModal = false }) => {
  const [charCount, setCharCount] = useState(0)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [isThinking, setIsThinking] = useState(false)
  const [examplePopoverOpen, setExamplePopoverOpen] = useState(false)
  const chatRootRef = useRef<HTMLDivElement | null>(null)
  const { remaining, isLoaded, consumeQuery } = useDemoLimit()
  const { sendMessage, messages, isConnected, isConnecting, error } = useDemoSocket()
  const selectedPaths = useDemoStore((state) => state.selectedPaths)
  const removePath = useDemoStore((state) => state.removePath)
  const clearPaths = useDemoStore((state) => state.clearPaths)
  const exampleChats = useDemoStore((state) => state.exampleChats)
  const currentExampleChatId = useDemoStore((state) => state.currentExampleChatId)
  const setExampleChat = useDemoStore((state) => state.setExampleChat)
  const clearChat = useDemoStore((state) => state.clearChat)

  const showExampleHeader = !hideTitle || exampleChats.length > 0
  
  // Read rate limit state from store
  const dailyLimit = useDemoStore((state) => state.dailyLimit)
  const limitReached = useDemoStore((state) => state.limitReached)
  const limitMessage = useDemoStore((state) => state.limitMessage)

  // Track character count from PromptInputProvider
  const CharCountTracker: React.FC = () => {
    const controller = usePromptInputController()
    
    useEffect(() => {
      setCharCount(controller.textInput.value.length)
    }, [controller.textInput.value, controller])
    
    return null
  }

  // Component to conditionally show example chat shortcuts only for new chat
  const GeneralPromptsWrapper: React.FC<{ hasAttachments: boolean; isDisabled: boolean }> = ({ hasAttachments, isDisabled }) => {
    // Only show for new chat (no messages and no current chat selected)
    const isNewChat = messages.length === 0 && currentExampleChatId === null
    
    if (hasAttachments || !isNewChat || isDisabled) {
      return null
    }
    
    return <SuggestedGeneralPrompts onSelectChat={setExampleChat} />
  }

  // Listen for thinking events - detect from messages
  useEffect(() => {
    const lastMessage = messages[messages.length - 1]
    if (lastMessage?.role === "assistant") {
      if (lastMessage.isStreaming) {
        setIsThinking(false) // Not thinking if streaming
      } else if (!lastMessage.content?.trim()) {
        setIsThinking(true) // Thinking if no content yet
      } else {
        setIsThinking(false)
      }
    } else {
      setIsThinking(false)
    }
  }, [messages])

  // Track previous limitReached state to detect transitions
  const prevLimitReachedRef = useRef(limitReached)

  // Automatically show upgrade modal when limit is reached (only if not disabled)
  useEffect(() => {
    // Only show modal when limitReached transitions from false to true
    if (!disableUpgradeModal && limitReached && !prevLimitReachedRef.current) {
      setShowUpgradeModal(true)
    }
    prevLimitReachedRef.current = limitReached
  }, [limitReached, disableUpgradeModal])


  const handleSubmit = async (message: { text: string; files: any[] }, event: React.FormEvent<HTMLFormElement>) => {
    if (!message.text.trim() || !isLoaded) return

    // Check if limit is reached - prevent submission, backend will emit DAILY_LIMIT if needed
    if (limitReached) {
      return
    }

    // Check if we have remaining questions (allow if remaining is null - server will handle it)
    if (remaining !== null && remaining <= 0) {
      return
    }

    // Try to consume a query - prevent submission if not allowed, backend will emit DAILY_LIMIT if needed
    const allowed = await consumeQuery()
    if (!allowed) {
      return
    }

    // Show thinking indicator
    setIsThinking(true)

    // Send message
    sendMessage(message.text.trim())
    
    // Clear selected paths after sending (they're included in the message)
    clearPaths()
  }

  // Check if any message is currently streaming
  const isStreaming = messages.some((msg) => msg.role === "assistant" && msg.isStreaming === true)

  // Allow input if connected and loaded, even if remaining is null (server will send DAILY_LIMIT exception when limit is reached)
  // Only disable if we know for sure the limit is reached or we're not connected/loaded
  // Don't disable just because remaining is null - that's the initial state before server sends DAILY_LIMIT exception
  // Also disable if streaming or thinking (like in the app)
  const isDisabled = !isLoaded || !isConnected || isConnecting || limitReached || (remaining !== null && remaining <= 0) || isStreaming || isThinking

  return (
    <>
      <TooltipProvider>
        <div className="w-full h-full flex flex-col bg-white text-[#041F53]" ref={chatRootRef} data-demo-chat-root>
          {showExampleHeader && (
            <div className="border-b bg-white px-2 py-2 shrink-0" data-demo-chat-header>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 flex-1">
                  <Popover open={examplePopoverOpen} onOpenChange={setExamplePopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="justify-between h-auto py-2 px-3 min-w-0 flex-1 text-left"
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <MessageSquare className="h-4 w-4 shrink-0" />
                          <span className="text-sm font-medium truncate text-left">
                            {currentExampleChatId
                              ? exampleChats.find((c) => c.id === currentExampleChatId)?.title || "Select a chat"
                              : "Select a chat"}
                          </span>
                        </div>
                        <ChevronDown className="h-4 w-4 shrink-0 ml-2" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-2 demo-popover z-[1000]" data-demo-popover>
                      <div className="px-2 py-1 text-xs font-semibold uppercase text-muted-foreground">
                        Chat history
                      </div>
                      <div className="mt-1 space-y-1">
                        {exampleChats.map((chat) => {
                          const isActive = chat.id === currentExampleChatId
                          return (
                            <button
                              key={chat.id}
                              type="button"
                              className={cn(
                                "w-full rounded-md px-2 py-1.5 text-left text-sm transition-colors flex items-center gap-2",
                                isActive ? "bg-accent" : "hover:bg-accent"
                              )}
                              onClick={() => {
                                setExampleChat(chat.id)
                                setExamplePopoverOpen(false)
                              }}
                            >
                              <span className="flex-1 min-w-0 truncate">{chat.title || "New Chat"}</span>
                            </button>
                          )
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-auto shrink-0 border-primary text-primary hover:bg-primary hover:text-primary-foreground py-2 px-3 gap-2"
                    onClick={() => clearChat()}
                  >
                    <Plus className="h-4 w-4" />
                    <span className="text-sm">New Chat</span>
                  </Button>
                </div>
              </div>
            </div>
          )}
          <div className="flex-1 flex flex-col min-h-0 bg-white" data-demo-chat-body>
            {/* Messages */}
            <Conversation className="flex-1 min-h-0 bg-white">
              <ConversationContent className={cn(messages.length === 0 && !isConnecting && "h-full")}>
                {messages.length === 0 && !isConnecting && (
                  <ExplorerChatEmpty />
                )}
                {messages.map((msg) => {
                  const hasContent = !!msg.content?.trim()
                  // Only render assistant message if it has content
                  if (msg.role === "assistant" && !hasContent) {
                    return null
                  }
                  return (
                    <Message key={msg.id} from={msg.role}>
                      {msg.role === "assistant" ? (
                        <MessageContent>
                          <MessageResponse>{msg.content}</MessageResponse>
                        </MessageContent>
                      ) : (
                        <div className="flex flex-col items-end gap-1">
                          <MessageContent>
                            <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                          </MessageContent>
                          {msg.attachments && msg.attachments.length > 0 && (
                            <MessageAttachmentsHover attachments={msg.attachments} />
                          )}
                        </div>
                      )}
                      {msg.role === "assistant" && hasContent && !msg.isStreaming && (
                        <MessageActions>
                          <MessageAction
                            tooltip="Copy message"
                            onClick={() => navigator.clipboard.writeText(msg.content)}
                          >
                            <Copy className="h-4 w-4" />
                          </MessageAction>
                        </MessageActions>
                      )}
                    </Message>
                  )
                })}
                {/* Thinking indicator */}
                {isThinking && (
                  <Message from="assistant">
                    <div className="w-fit rounded-lg bg-white px-4 py-3 text-sm flex items-center gap-2">
                      <ThinkingRow />
                    </div>
                  </Message>
                )}
              </ConversationContent>
              <ConversationScrollButton />
            </Conversation>

            {/* Input area */}
            <div className="shrink-0 p-2 max-w-full bg-white" data-demo-chat-input>
              <PromptInputProvider>
                <CharCountTracker />
                
                {/* Error banner */}
                {error && (
                  <div className="mb-2 p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                    <p className="font-medium">Something went wrong. Try again later.</p>
                  </div>
                )}
                
                {/* Limit reached warning banner */}
                {limitReached && limitMessage && (
                  <div className="mb-2 p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                    <p className="font-medium">{limitMessage}</p>
                  </div>
                )}
                
                {/* Tree attachments display */}
                {selectedPaths.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2 p-2 border rounded-md bg-white">
                    {selectedPaths.map((path) => (
                      <div
                        key={path}
                        className="group relative flex h-6 items-center gap-1 rounded-md border border-border px-1.5 text-xs hover:bg-accent transition-colors"
                      >
                        <FileIcon className="h-3 w-3 text-gray-600 flex-shrink-0" />
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="truncate max-w-[150px]">{path.split('/').pop() || path}</span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{path}</p>
                          </TooltipContent>
                        </Tooltip>
                        <button
                          onClick={() => removePath(path)}
                          className="h-4 w-4 flex items-center justify-center rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive ml-0.5"
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </div>
                    ))}
                    {/* Clear all button */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={clearPaths}
                          className="flex h-6 items-center gap-1 rounded-md border border-destructive/50 bg-destructive/10 px-1.5 text-xs hover:bg-destructive/20 transition-colors text-destructive"
                        >
                          <X className="h-3 w-3" />
                          <span className="font-medium">Clear all</span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Remove all attachments</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                )}

                <GeneralPromptsWrapper hasAttachments={selectedPaths.length > 0} isDisabled={isDisabled} />

                {selectedPaths.length > 0 && (
                  <SuggestedFilePrompts attachmentCount={selectedPaths.length} />
                )}

                <PromptInput 
                  onSubmit={handleSubmit} 
                  className="bg-white overflow-x-hidden max-w-full"
                >
                  <PromptInputTextareaWithTruncate
                    className="min-h-[2.5rem] max-h-32 resize-none"
                    placeholder={
                      isDisabled
                        ? limitReached
                          ? "Daily limit reached. Sign up for more!"
                          : remaining === 0
                            ? "You've used all questions. Sign up for more!"
                            : !isConnected
                              ? "Connecting..."
                              : "Ask a question about your repository..."
                        : "Ask a question about your repository..."
                    }
                    disabled={isDisabled}
                    maxLength={1000}
                  />
                  <PromptInputFooter className="justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground text-xs">
                        {charCount}/1000
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <PromptInputSubmit disabled={isDisabled} />
                    </div>
                  </PromptInputFooter>
                </PromptInput>

                {(limitReached || (remaining !== null && remaining === 0)) && (
                  <Button
                    onClick={() => onSignup()}
                    variant="default"
                    className="w-full mt-2"
                    size="sm"
                  >
                    Sign up
                  </Button>
                )}
              </PromptInputProvider>
            </div>
          </div>
        </div>
      </TooltipProvider>

      {!disableUpgradeModal && (
        <UpgradeModal
          open={showUpgradeModal}
          onOpenChange={setShowUpgradeModal}
          onSignup={onSignup}
        />
      )}
    </>
  )
}
